import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { publishToGallery } from '../../../services/computeWallet';

const TYPE_CONFIG = {
  video: { icon: 'Video', color: '#6366F1', label: 'Video', downloadExt: 'mp4' },
  music: { icon: 'Music', color: '#8B5CF6', label: 'Music', downloadExt: 'mp3' },
  game: { icon: 'Gamepad2', color: '#10B981', label: 'Game', downloadExt: 'zip' },
  animation: { icon: 'Film', color: '#F59E0B', label: 'Animation', downloadExt: 'mp4' },
  image: { icon: 'Image', color: '#EC4899', label: 'Image', downloadExt: 'png' },
};

const FILTER_TABS = [
  { id: 'all', label: 'All', icon: 'Layers' },
  { id: 'video', label: 'Videos', icon: 'Video' },
  { id: 'music', label: 'Music', icon: 'Music' },
  { id: 'game', label: 'Games', icon: 'Gamepad2' },
];

function CreationCard({ asset, onDownload, onPublish, isPublishing }) {
  const config = TYPE_CONFIG?.[asset?.type] || TYPE_CONFIG?.video;
  return (
    <div
      className="rounded-2xl overflow-hidden border transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 group"
      style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9', background: `${config?.color}10` }}>
        {asset?.thumbnail ? (
          <Image
            src={asset?.thumbnail}
            alt={`Thumbnail for ${asset?.title}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name={config?.icon} size={36} color={config?.color} />
          </div>
        )}
        {/* Type badge */}
        <span
          className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{ background: config?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
        >
          {config?.label}
        </span>
        {/* Published badge */}
        {asset?.published && (
          <span
            className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ background: 'rgba(16,185,129,0.9)', color: 'white', fontFamily: 'var(--font-caption)' }}
          >
            Published
          </span>
        )}
      </div>
      {/* Content */}
      <div className="p-3">
        <h3 className="text-sm font-bold truncate mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
          {asset?.title}
        </h3>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            <Icon name="Eye" size={11} color="var(--color-muted-foreground)" />
            <span style={{ fontFamily: 'var(--font-data)' }}>{asset?.views?.toLocaleString?.() || 0}</span>
          </div>
          <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            {asset?.createdAt ? new Date(asset?.createdAt)?.toLocaleDateString() : ''}
          </span>
        </div>
        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onDownload?.(asset)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: `${config?.color}15`, color: config?.color, fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Download" size={12} color={config?.color} />
            Download
          </button>
          {!asset?.published ? (
            <button
              onClick={() => onPublish?.(asset)}
              disabled={isPublishing}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50"
              style={{ background: 'rgba(99,102,241,0.12)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}
            >
              <Icon name={isPublishing ? 'Loader' : 'Globe'} size={12} color="var(--color-primary)" />
              {isPublishing ? 'Publishing...' : 'Publish'}
            </button>
          ) : (
            <div
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold"
              style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', fontFamily: 'var(--font-caption)' }}
            >
              <Icon name="CheckCircle2" size={12} color="#10B981" />
              Live
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MyCreationsTab() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [publishingId, setPublishingId] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadAssets = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data: assetData, error: assetError } = await supabase
        ?.from('generated_assets')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (!assetError && assetData?.length > 0) {
        setAssets(assetData?.map((a) => ({
          id: a?.id,
          title: a?.title || a?.job_id || 'Untitled',
          type: a?.asset_type || 'video',
          thumbnail: a?.thumbnail_url,
          outputUrl: a?.output_url,
          views: a?.view_count || 0,
          createdAt: a?.created_at,
          published: a?.is_published || false,
          prompt: a?.prompt,
        })));
        setLoading(false);
        return;
      }

      const { data: musicData } = await supabase
        ?.from('generated_music')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      const combined = [
        ...(musicData || [])?.map((m) => ({
          id: `music_${m?.id}`,
          title: m?.song_title || 'Untitled Track',
          type: 'music',
          thumbnail: null,
          outputUrl: m?.audio_url,
          views: 0,
          createdAt: m?.created_at,
          published: false,
          prompt: m?.lyrics || `${m?.style} ${m?.mood} music`,
          style: m?.style,
          mood: m?.mood,
          tempo: m?.tempo,
        })),
      ];
      setAssets(combined);
    } catch (err) {
      console.error('[MyCreationsTab] loadAssets error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  const handleDownload = (asset) => {
    const config = TYPE_CONFIG?.[asset?.type] || TYPE_CONFIG?.video;
    const filename = `${asset?.title?.replace(/\s+/g, '_') || 'asset'}.${config?.downloadExt}`;
    if (asset?.outputUrl) {
      const a = document.createElement('a');
      a.href = asset?.outputUrl;
      a.download = filename;
      a.target = '_blank';
      a?.click();
    } else {
      showToast(`Download ready when ${config?.label?.toLowerCase()} is fully generated`, 'info');
    }
  };

  const handlePublish = async (asset) => {
    if (!user?.id) { showToast('Sign in to publish', 'error'); return; }
    setPublishingId(asset?.id);
    try {
      await publishToGallery({
        userId: user?.id,
        jobId: asset?.id?.toString(),
        title: asset?.title,
        description: asset?.prompt?.slice(0, 200) || '',
        generationType: asset?.type,
        thumbnailUrl: asset?.thumbnail || null,
        outputUrl: asset?.outputUrl || null,
        prompt: asset?.prompt || '',
        allowRemix: true,
        tags: [asset?.type],
      });
      setAssets((prev) => prev?.map((a) => a?.id === asset?.id ? { ...a, published: true } : a));
      showToast(`"${asset?.title}" published to Community Gallery!`);
    } catch (err) {
      showToast('Failed to publish. Please try again.', 'error');
    } finally {
      setPublishingId(null);
    }
  };

  const filtered = filter === 'all' ? assets : assets?.filter((a) => a?.type === filter);

  return (
    <div className="flex flex-col gap-4">
      {/* Toast */}
      {toast && (
        <div
          className="flex items-center gap-2 p-3 rounded-xl"
          style={{
            background: toast?.type === 'success' ? 'rgba(16,185,129,0.12)' : toast?.type === 'error' ? 'rgba(239,68,68,0.12)' : 'rgba(99,102,241,0.12)',
            border: `1px solid ${toast?.type === 'success' ? '#10B981' : toast?.type === 'error' ? '#EF4444' : '#6366F1'}40`,
          }}
        >
          <Icon name={toast?.type === 'success' ? 'CheckCircle2' : toast?.type === 'error' ? 'AlertCircle' : 'Info'} size={14} color={toast?.type === 'success' ? '#10B981' : toast?.type === 'error' ? '#EF4444' : '#6366F1'} />
          <span className="text-xs" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{toast?.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>My Creations</h2>
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>All your generated videos, music, and games</p>
        </div>
        <button
          onClick={loadAssets}
          disabled={loading}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
          style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
        >
          <Icon name={loading ? 'Loader' : 'RefreshCw'} size={12} color="var(--color-muted-foreground)" />
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--color-muted)' }}>
        {FILTER_TABS?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setFilter(tab?.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: filter === tab?.id ? 'var(--color-card)' : 'transparent',
              color: filter === tab?.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
              fontFamily: 'var(--font-heading)',
            }}
          >
            <Icon name={tab?.icon} size={12} color={filter === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
            {tab?.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader" size={24} color="var(--color-primary)" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-3" style={{ border: '1px dashed var(--color-border)', borderRadius: 16 }}>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.1)' }}>
            <Icon name="Layers" size={26} color="var(--color-primary)" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>No creations yet</p>
            <p className="text-xs mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Generate music, videos, or games to see them here</p>
          </div>
        </div>
      )}

      {/* Creations grid */}
      {!loading && filtered?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered?.map((asset) => (
            <CreationCard
              key={asset?.id}
              asset={asset}
              onDownload={handleDownload}
              onPublish={handlePublish}
              isPublishing={publishingId === asset?.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
